import api from '@/services/api';
import type { Plan, PlanProduct, PlanServiceRow, PaginatedPlans, ProductType } from '../types/plan.types';
import type { ServiceFormSubmitValues } from '../schemas/service.schema';

const smrPath = '/smr';

export interface PlanListParams {
    search?: string;
    page?: number;
    per_page?: number;
    status?: string;
    sort_by?: string;
    sort_dir?: string;
}

export interface PlanPayload {
    name: string;
    description?: string;
    status?: string;
    is_looped?: boolean;
}

export type PlanServiceCreatePayload = ServiceFormSubmitValues;

export interface SmrProductSearchResult {
    id: string;
    name: string;
}

export const fetchPlans = async (params: PlanListParams): Promise<PaginatedPlans> =>
    (await api.get(`${smrPath}/plans`, { params })).data;

export const fetchPlan = async (id: string): Promise<Plan> =>
    (await api.get(`${smrPath}/plans/${id}`)).data.data;

export const createPlan = async (payload: PlanPayload): Promise<Plan> =>
    (await api.post(`${smrPath}/plans`, payload)).data.data;

export const updatePlan = async (id: string, payload: PlanPayload): Promise<Plan> =>
    (await api.put(`${smrPath}/plans/${id}`, payload)).data.data;

export const deletePlan = async (id: string): Promise<void> => {
    await api.delete(`${smrPath}/plans/${id}`);
};

export const retirePlan = async (id: string): Promise<Plan> =>
    (await api.patch(`${smrPath}/plans/${id}/retire`)).data.data;

export const fetchPlanProducts = async (planId: string): Promise<PlanProduct[]> =>
    (await api.get(`${smrPath}/plans/${planId}/products`)).data.data ?? [];

export const attachProductToPlan = async (planId: string, productId: string): Promise<void> => {
    await api.post(`${smrPath}/plans/${planId}/products`, { product_id: productId });
};

export const detachProductFromPlan = async (planId: string, productId: string): Promise<void> => {
    await api.delete(`${smrPath}/plans/${planId}/products/${productId}`);
};

const byPosition = (a: PlanServiceRow, b: PlanServiceRow) => a.position - b.position;

export const fetchPlanServices = async (planId: string): Promise<PlanServiceRow[]> =>
    (((await api.get(`${smrPath}/plans/${planId}/services`)).data.data ?? []) as PlanServiceRow[])
        .slice()
        .sort(byPosition);

export const createPlanService = async (
    planId: string,
    payload: PlanServiceCreatePayload,
): Promise<PlanServiceRow> =>
    (await api.post(`${smrPath}/plans/${planId}/services`, payload)).data.data;

export const fetchProductTypes = async (): Promise<ProductType[]> =>
    (await api.get(`${smrPath}/product-types`)).data.data ?? [];

export const searchSmrProducts = async (search: string): Promise<SmrProductSearchResult[]> => {
    const { data } = await api.get<{ data: SmrProductSearchResult[] }>(`${smrPath}/products`, {
        params: { search, per_page: 20 },
    });
    return data.data ?? [];
};
